/**
 * Voice AI — Twilio Service
 * Handles Twilio-specific API calls: webhook config, test call, connection test, number listing.
 * Components should call this service instead of making direct fetch calls.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class TwilioService {
  /**
   * Configure webhook URL on Twilio numbers.
   * @param {Array|string} numberIds - Array of DB ids or "all"
   * @returns {Promise<Object>} { success, configured, webhook_url, results }
   */
  async configureWebhooks(numberIds = 'all') {
    const res = await authService.apiFetch(`${API_BASE}/webhook-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ number_ids: numberIds })
    });
    return res.json();
  }

  /**
   * Make a test call using inline TwiML (no webhook).
   * @param {string} fromNumber - E.164 from number
   * @param {string} toNumber - E.164 to number
   * @returns {Promise<Object>} { success, call: { sid, status } }
   */
  async makeTestCall(fromNumber, toNumber) {
    const res = await authService.apiFetch(`${API_BASE}/make-call.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from_number: fromNumber, to_number: toNumber })
    });
    return res.json();
  }

  /**
   * Test Twilio connection with provided credentials.
   * @param {string} accountSid - Twilio Account SID
   * @param {string} authToken - Twilio Auth Token
   * @returns {Promise<Object>} { success, account }
   */
  async testConnection(accountSid, authToken) {
    const res = await authService.apiFetch(`${API_BASE}/twilio-test.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_sid: accountSid, auth_token: authToken })
    });
    return res.json();
  }

  /**
   * Hang up an active call via Twilio API.
   * @param {string} callSid - Twilio Call SID
   * @returns {Promise<Object>} { success, message }
   */
  async hangupCall(callSid) {
    const res = await authService.apiFetch(`${API_BASE}/hangup-call.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ call_sid: callSid })
    });
    return res.json();
  }

  /**
   * List incoming phone numbers from Twilio account.
   * @returns {Promise<Object>} { success, numbers }
   */
  async listTwilioNumbers() {
    const res = await authService.apiFetch(`${API_BASE}/twilio-numbers.php`);
    return res.json();
  }
}

export const twilioService = new TwilioService();
